import { z } from 'zod';
import { createApiHeaders } from '../api-core';
import { getCurrentGatewayUrl } from '../../../utils/environmentDetection';
import {
  ReassociateHarEntriesConflictError,
  ReassociateHarEntriesConflictSchema,
  ReassociateHarEntriesInputSchema,
  ReassociateHarEntriesSuccessSchema,
  type ReassociateHarEntriesConflict,
  type ReassociateHarEntriesInput,
  type ReassociateHarEntriesSuccess,
} from './reassociate-har-entries.schemas';

const ErrorMessageSchema = z.object({
  message: z.string(),
});

type ParsedReassociateBody = {
  success?: ReassociateHarEntriesSuccess;
  conflict?: ReassociateHarEntriesConflict;
  message?: string;
};

const parseResponseBody = (text: string): ParsedReassociateBody => {
  const json: unknown = JSON.parse(text);
  const conflict = ReassociateHarEntriesConflictSchema.safeParse(json);

  if (conflict.success) {
    return { conflict: conflict.data };
  }

  const success = ReassociateHarEntriesSuccessSchema.safeParse(json);

  if (success.success) {
    return { success: success.data };
  }

  const message = ErrorMessageSchema.safeParse(json);

  if (message.success) {
    return { message: message.data.message };
  }

  return {};
};

export const reassociateHarEntries = async (
  input: ReassociateHarEntriesInput,
): Promise<ReassociateHarEntriesSuccess> => {
  const parsedInput = ReassociateHarEntriesInputSchema.parse(input);
  const headers = await createApiHeaders();
  const response = await fetch(
    `${getCurrentGatewayUrl()}/orchestrator/endpoints/id/${encodeURIComponent(parsedInput.id)}/har-entries/reassociate`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({
        harEntryIds: parsedInput.harEntryIds,
        target: parsedInput.target,
      }),
    },
  );
  const parsedBody = parseResponseBody(await response.text());

  if (response.status === 409 && parsedBody.conflict) {
    throw new ReassociateHarEntriesConflictError(parsedBody.conflict);
  }

  if (response.ok && parsedBody.success) {
    return parsedBody.success;
  }

  throw new Error(
    parsedBody.message ?? `API call failed with status: ${response.status}`,
  );
};

export const portalReassociateHarEntriesApi = {
  reassociateHarEntries,
};
